import { Lock } from 'lucide-react'
import { cn } from '@/lib/utils'

interface VerticalTabProps {
  label: string
  active: boolean
  onClick: () => void
  /** Extra classes (e.g. width / extra bottom padding to make room for a kebab). */
  className?: string
  /** Plan-locked: mark it, but keep it clickable so the click can sell. */
  locked?: boolean
}

export function VerticalTab({ label, active, onClick, className, locked }: VerticalTabProps) {
  return (
    <button
      onClick={onClick}
      // writing-mode lives on the inner SPAN, not the button: WebKit does not
      // properly support vertical writing modes on form controls, so a vertical
      // button gets horizontal intrinsic sizing in Safari - long labels wrap
      // into a second column and the whole rail widens. Regular elements are
      // fine in every engine.
      className={cn(
        // Fixed width (not px padding + text metrics): Safari sizes vertical
        // text fatter than Chrome, so any intrinsic sizing renders a wider
        // rail on WebKit. 32px is the old Chrome look; the label column just
        // centers inside it in every engine.
        // py-3 restores the old chip length: the original button was itself in
        // vertical writing mode, where its px-3 (padding-INLINE) padded the
        // label's ends; with the button horizontal that job moves to py.
        'flex w-8 items-center justify-center py-3 text-[11px] font-medium tracking-wide transition-colors select-none rounded',
        // Only a locked tab has a second child, so only it needs to stack.
        locked && 'flex-col gap-1',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-hover',
        className,
      )}
      style={active ? { backgroundColor: 'hsl(var(--accent))' } : undefined}
    >
      {/* leading-none pins the vertical line box to the glyph size - Safari's
          default line-height for vertical text is fatter than Chrome's, which
          otherwise widens every tab (and the whole rail) on WebKit. */}
      <span className="whitespace-nowrap leading-none [writing-mode:vertical-rl]">{label}</span>
      {/* aria-hidden so the tab's accessible name stays the plain label. */}
      {locked && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />}
    </button>
  )
}
